window.centerIceProducts = window.centerIceProducts || {
  'prod-liner': 'premium-rink-liner',
  liner: 'premium-rink-liner',
  'prod-flooder': 'ice-flooder',
  flooder: 'ice-flooder',
  'prod-net': 'heavy-duty-goalie-net',
  net: 'heavy-duty-goalie-net',
  'prod-safety': 'protective-netting',
  safety: 'protective-netting',
  'prod-tyke': 'tykeskater-support',
  tyke: 'tykeskater-support',
  'prod-assist': 'skating-assist-aid',
  assist: 'skating-assist-aid',
};

const moneyFormatter = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'CAD',
});

function formatCartMoney(cents) {
  return moneyFormatter.format((Number(cents) || 0) / 100);
}

function escapeHtml(value) {
  return String(value || '').replace(/[&<>"']/g, function(character) {
    return {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[character];
  });
}

function getCartRoutes() {
  return {
    add: window.routes?.cart_add_url || '/cart/add.js',
    change: window.routes?.cart_change_url || '/cart/change.js',
    cart: window.routes?.cart_url || '/cart',
  };
}

function getProductHandle(id) {
  return window.centerIceProducts[id] || id;
}

async function fetchProductByHandle(handle) {
  const response = await fetch(`/products/${handle}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Product "${handle}" was not found.`);
  return response.json();
}

async function fetchCart() {
  const response = await fetch('/cart.js', {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Cart could not be loaded.');
  return response.json();
}

async function changeCartLine(line, quantity) {
  const response = await fetch(getCartRoutes().change, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ line, quantity }),
  });
  if (!response.ok) throw new Error('Cart could not be updated.');
  const cart = await response.json();
  renderShopCart(cart);
  return cart;
}

function setCartBusy(isBusy) {
  // Only re-enable buttons this function disabled — "Sold Out"/"Unavailable"
  // buttons are disabled elsewhere and must stay that way.
  document.querySelectorAll('[data-cart-action], .btn-add-to-cart, .product-detail-add').forEach(function(button) {
    if (isBusy) {
      if (button.disabled) return;
      button.disabled = true;
      button.dataset.cartBusy = 'true';
      button.classList.add('is-loading');
    } else if (button.dataset.cartBusy) {
      button.disabled = false;
      delete button.dataset.cartBusy;
      button.classList.remove('is-loading');
    }
  });
}

function toggleCartDrawer(isOpen) {
  const overlay = document.getElementById('cart-overlay');
  if (!overlay) return;
  overlay.style.display = isOpen ? 'block' : 'none';
  overlay.classList.toggle('open', isOpen);
  document.body.classList.toggle('cart-open', isOpen);
  if (isOpen) refreshShopCart();
}

function closeCartOnOverlay(event) {
  if (event.target === document.getElementById('cart-overlay')) toggleCartDrawer(false);
}

async function addVariantToCart(variantId, quantity) {
  setCartBusy(true);

  try {
    const response = await fetch(getCartRoutes().add, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Product could not be added.');
    }

    await refreshShopCart();
    toggleCartDrawer(true);
  } catch (error) {
    showCartMessage(error.message || 'Something went wrong adding this product.');
  } finally {
    setCartBusy(false);
  }
}

async function addProductToCart(id) {
  const handle = getProductHandle(id);
  setCartBusy(true);

  try {
    const product = await fetchProductByHandle(handle);
    // A quick-add can't pick options — send shoppers to the product page
    // instead of silently carting an arbitrary variant.
    if (product.variants.length > 1) {
      window.location.href = `/products/${handle}`;
      return;
    }
    const variant = product.variants.find(function(item) { return item.available; }) || product.variants[0];
    if (!variant) throw new Error('This product does not have an available variant.');
    await addVariantToCart(variant.id, 1);
  } catch (error) {
    showCartMessage(error.message || 'Something went wrong adding this product.');
  } finally {
    setCartBusy(false);
  }
}

function showCartMessage(message) {
  const container = document.getElementById('cart-items-container');
  if (!container) return;
  let banner = container.querySelector('.cart-error-banner');
  if (!banner) {
    banner = document.createElement('div');
    banner.className = 'cart-error-banner';
    banner.setAttribute('role', 'alert');
    container.prepend(banner);
  }
  banner.textContent = message;
  const overlay = document.getElementById('cart-overlay');
  if (overlay) {
    overlay.style.display = 'block';
    overlay.classList.add('open');
    document.body.classList.add('cart-open');
  }
}

function renderShopCart(cart) {
  const container = document.getElementById('cart-items-container');
  const subtotalEl = document.getElementById('cart-subtotal');
  if (!container) return;

  // Desktop (in the nav menu) and mobile (in the header bar) cart icons.
  const count = cart ? cart.item_count : 0;
  document.querySelectorAll('.cart-nav-trigger').forEach(function(cartIcon) {
    const badge = cartIcon.querySelector('.cart-badge-count');
    if (badge) badge.textContent = count;
    cartIcon.style.display = count > 0 ? 'inline-flex' : 'none';
    cartIcon.setAttribute('aria-label', `Open cart, ${count} ${count === 1 ? 'item' : 'items'}`);
  });
  if (subtotalEl) subtotalEl.textContent = formatCartMoney(cart ? cart.total_price : 0);

  if (!cart || !cart.items || !cart.items.length) {
    container.innerHTML = '<div class="empty-cart-message"><p>Your shopping cart is empty.</p><p><a href="/pages/shop">Continue shopping</a></p></div>';
    return;
  }

  container.innerHTML = cart.items.map(function(item, index) {
    const title = escapeHtml(item.product_title);
    const variant = escapeHtml(item.variant_title);
    const url = item.url || '#';
    const image = item.image || item.featured_image?.url || '';
    const imageHtml = image
      ? `<img src="${image}" alt="${title}">`
      : '<div class="cart-item-image-placeholder"></div>';
    const variantTitle = variant && variant !== 'Default Title'
      ? `<p class="cart-item-variant">${variant}</p>`
      : '';

    return `<div class="cart-item">
      <a class="cart-item-image" href="${url}">${imageHtml}</a>
      <div class="cart-item-info">
        <h3><a href="${url}">${title}</a></h3>
        ${variantTitle}
        <div class="cart-item-actions">
          <div class="cart-quantity-controls" aria-label="Quantity for ${title}">
            <button class="cart-quantity-btn" type="button" data-cart-action="decrease" data-line="${index + 1}" aria-label="Decrease quantity">-</button>
            <span class="cart-quantity-value">${item.quantity}</span>
            <button class="cart-quantity-btn" type="button" data-cart-action="increase" data-line="${index + 1}" aria-label="Increase quantity">+</button>
          </div>
          <div class="cart-item-price">${formatCartMoney(item.final_line_price)}</div>
          <button class="cart-remove-btn" type="button" data-cart-action="remove" data-line="${index + 1}">Remove</button>
        </div>
      </div>
    </div>`;
  }).join('');
}

let cartLoadRetried = false;

async function refreshShopCart() {
  try {
    const cart = await fetchCart();
    renderShopCart(cart);
    return cart;
  } catch (error) {
    // Only surface the error if the shopper is looking at the drawer —
    // a transient failure on page load must not cover the page with it.
    // The cart icon only appears after a successful load, so retry once:
    // without it a failed initial load leaves the cart unreachable.
    if (document.body.classList.contains('cart-open')) {
      showCartMessage('Cart could not be loaded. Please try again.');
    } else if (!cartLoadRetried) {
      cartLoadRetried = true;
      setTimeout(refreshShopCart, 3000);
    }
  }
}

function wireShopCart() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer) drawer.dataset.cartLayout = '3';

  document.addEventListener('click', function(event) {
    const addButton = event.target.closest('.btn-add-to-cart, .product-detail-add');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Product page: trust the form's selected variant and quantity.
      // Never fall through from a product form to the quick-add path —
      // an empty variant id means "no matching variant", not "guess one".
      const form = addButton.closest('form[action*="/cart/add"]');
      if (form) {
        const formVariantId = Number(form.querySelector('[name="id"]')?.value);
        if (formVariantId) {
          const formQuantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
          addVariantToCart(formVariantId, formQuantity);
        } else {
          showCartMessage('Please select an available option.');
        }
        return;
      }

      const card = addButton.closest('[data-product-id], .product-card-improved, .product-detail-shell');
      const id = addButton.dataset.productId || card?.dataset.productId || card?.id || new URLSearchParams(location.search).get('product');
      if (id) addProductToCart(id);
      return;
    }

    const cartToggle = event.target.closest('.cart-nav-trigger');
    if (cartToggle) {
      event.preventDefault();
      toggleCartDrawer(true);
      return;
    }

    const cartAction = event.target.closest('[data-cart-action]');
    if (!cartAction) return;
    event.preventDefault();
    const line = Number(cartAction.dataset.line);
    const quantityEl = cartAction.closest('.cart-item')?.querySelector('.cart-quantity-value');
    const quantity = Number(quantityEl?.textContent || 0);
    let nextQuantity = quantity;
    if (cartAction.dataset.cartAction === 'increase') nextQuantity = quantity + 1;
    if (cartAction.dataset.cartAction === 'decrease') nextQuantity = Math.max(0, quantity - 1);
    if (cartAction.dataset.cartAction === 'remove') nextQuantity = 0;
    setCartBusy(true);
    changeCartLine(line, nextQuantity)
      .catch(function() { showCartMessage('Your cart could not be updated. Please try again.'); })
      .finally(function() { setCartBusy(false); });
  }, true);

  document.addEventListener('keydown', function(event) {
    if (event.key === 'Escape') toggleCartDrawer(false);
  });

  refreshShopCart();
}

document.addEventListener('DOMContentLoaded', wireShopCart);
window.addProductToCart = addProductToCart;
window.toggleCartDrawer = toggleCartDrawer;
window.closeCartOnOverlay = closeCartOnOverlay;
window.renderCart = refreshShopCart;
